import React, { FC, useState, useEffect } from 'react';
import injectSheet from 'react-jss';
import CheckBox from 'Components/CheckBox';
import { FilterRadioHidden } from 'Components/FilterRadioHidden';
import ArrowIcon from 'Assets/ui/Accordion/downArrow.svg';
import MinusIcon from 'Assets/ui/minus.svg';
import PlusIcon from 'Assets/ui/plus.svg';
import { TweenMax, Power4 } from 'gsap';
import { handleAccessibilityClick } from 'Utils/handleAccessibilityClick';
import styleSheet from './styles';

interface Props {
  classes: any;
  title: string;
  id: string;
  isOpen: boolean;
  accordionCallback: () => void;
  children?: any;
  useTopDivider?: boolean;
  useBottomDivider?: boolean;
  accordionType:
    | 'Root'
    | 'Section'
    | 'Checkbox'
    | 'FilterRadioHidden'
    | 'Silent';
  isActive?: boolean;
  useSelectAllButton?: boolean;
  useClearButton?: boolean;
  selectAll?: () => void;
  clear?: () => void;
  // Using these for styling unfortunately
  // eslint-disable-next-line react/no-unused-prop-types
  overrideStyles?: {
    width?: string;
    marginLeft?: string;
  };
  // eslint-disable-next-line react/no-unused-prop-types
  overrideStylesChildContainer?: {
    marginLeft?: string;
  };
  // eslint-disable-next-line react/no-unused-prop-types
  color?: string;
}

const Accordion: FC<Props> = (props: Props) => {
  const [isHovering, setIsHovering] = useState(false);

  const {
    classes,
    title,
    id,
    isOpen,
    accordionCallback,
    children,
    useTopDivider,
    useBottomDivider,
    accordionType,
    isActive,
    useSelectAllButton,
    useClearButton,
    selectAll,
    clear,
  } = props;

  useEffect(() => {
    const element = document.querySelector(`.expandable${id}`);
    if (element) {
      if (isOpen) {
        TweenMax.killTweensOf(element);
        TweenMax.set(element, {
          height: 'auto',
          opacity: '1',
          display: 'block',
        });
        TweenMax.from(element, 0.75, {
          height: '0',
          opacity: '0',
          ease: Power4.easeOut,
          onComplete: () => {
            TweenMax.set(element, { height: 'auto', overflow: 'visible' });
          },
        });
      } else {
        TweenMax.to(element, 0.75, {
          display: 'none',
        });
        TweenMax.to(element, 0.75, {
          overflow: 'hidden',
          height: '0',
          opacity: '0',
          ease: Power4.easeOut,
        });
      }
    }
  }, [isOpen]);

  const getTitleStyling = () => {
    switch (accordionType) {
      case 'Root':
        return <span className={`${classes.rootAccordionTitle}`}>{title}</span>;
      case 'Section':
        return (
          <span
            className={`${classes.accordionSectionTitle} ${
              isActive && classes.accordionSectionTitleActive
            }`}
          >
            {title}
          </span>
        );
      case 'Checkbox':
        return undefined;
      case 'FilterRadioHidden':
        return undefined;
      default:
        return (
          <span
            className={`${classes.accordionSectionTitle} ${
              isActive && classes.accordionSectionTitleActive
            }`}
          >
            {title}
          </span>
        );
    }
  };

  const getIndicatorStyling = () => {
    switch (accordionType) {
      case 'Root':
        return (
          <div className={`${classes.rootAccordionIndicatorContainer}`}>
            {(isOpen && <MinusIcon data-testid="minusIcon" />) || (
              <PlusIcon data-testid="plusIcon" />
            )}
          </div>
        );
      case 'Section':
        return (
          <div className={`${classes.accordionSectionIndicatorContainer}`}>
            <ArrowIcon
              className={`${classes.accordionSectionChevron} ${
                isOpen && classes.accordionSectionChevronRotate
              } ${isActive && classes.accordionSectionChevronActive}`}
            />
          </div>
        );
      case 'Checkbox':
        return (
          <div className={`${classes.accordionSectionIndicatorContainer}`}>
            <CheckBox isChecked={isOpen} label={title} />
          </div>
        );

      case 'FilterRadioHidden':
        return (
          <div className={`${classes.accordionSectionIndicatorContainer}`}>
            <FilterRadioHidden
              selected={isOpen}
              label={title}
              value={title}
              overrideStyles={{ paddingLeft: '0px' }}
            />
          </div>
        );
      default:
        return undefined;
    }
  };

  const handleAccordionClick = (event: any) => {
    if (handleAccessibilityClick(event)) {
      accordionCallback();
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleSelectAllClick = (event: any) => {
    event.stopPropagation();
    selectAll();
  };

  const handleSelectAllKeyUp = (event: any) => {
    if (event.keyCode === 13) {
      event.stopPropagation();
      selectAll();
    }
  };

  const handleClearClick = (event: any) => {
    event.stopPropagation();
    clear();
  };

  const handleClearKeyUp = (event: any) => {
    if (event.keyCode === 13) {
      event.stopPropagation();
      clear();
    }
  };

  const accordionPanelId = `accordion-panel-${id}`;

  return (
    <div
      className={`${
        (accordionType === 'Root' && classes.rootAccordion) ||
        classes.accordionSection
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="accordion"
    >
      <div
        className={`${useTopDivider && classes.topDivider}`}
        data-testid="classes.topDivider"
      />

      <div className={classes.accordionTitleContainer}>
        <div
          className={`${
            (accordionType === 'Root' && classes.rootAccordionTitleContainer) ||
            classes.accordionSectionTitleContainer
          }`}
          onClick={handleAccordionClick}
          onKeyDown={handleAccordionClick}
          tabIndex={accordionType !== 'Checkbox' && 0}
          role="button"
          data-testid="classes.accordionTitleContainer"
          aria-controls={accordionPanelId}
          aria-expanded={isOpen}
        >
          {getTitleStyling()}
          {getIndicatorStyling()}
        </div>
        <div
          className={`${classes.buttonContainer} ${
            (!useClearButton || !isHovering || !isOpen) && classes.hidden
          }`}
        >
          <div
            className={`${classes.button} ${classes.clearButton}`}
            onClick={handleClearClick}
            onKeyUp={handleClearKeyUp}
            tabIndex={0}
            role="button"
            data-testid="clearButton"
          >
            <span className={`${classes.buttonText}`}>Clear</span>
          </div>
        </div>
        <div
          className={`${classes.buttonContainer} ${
            (!useSelectAllButton || !isHovering || !isOpen) && classes.hidden
          }`}
        >
          <div
            className={`${classes.button} ${classes.selectAllButton}`}
            onClick={handleSelectAllClick}
            onKeyUp={handleSelectAllKeyUp}
            tabIndex={0}
            role="button"
            data-testid="selectAllButton"
          >
            <span className={`${classes.buttonText}`}>Select All</span>
          </div>
        </div>
      </div>

      <div
        id={accordionPanelId}
        className={`expandable${id} ${classes.accordionContent} ${
          !isOpen && classes.accordionContentHidden
        }`}
      >
        {children}
      </div>

      <div
        className={`${useBottomDivider && classes.bottomDivider}`}
        data-testid="classes.bottomDivider"
      />
    </div>
  );
};

Accordion.defaultProps = {
  children: null,
  useTopDivider: false,
  useBottomDivider: false,
  isActive: false,
  useSelectAllButton: false,
  useClearButton: false,
  selectAll: () => {},
  clear: () => {},
  overrideStyles: {},
  overrideStylesChildContainer: {},
  color: undefined,
};

export default injectSheet(styleSheet)(Accordion);
